/**
 * DateGroupedList — date-grouped row list with expandable per-day details,
 * matches the Penpot "Service Requests" / "Projects" widget structure.
 *
 * Layout per Penpot (bounds-verified):
 *   - Optional column header (light-grey bg, rounded, "Date" + count label)
 *   - Collapsed rows: date text (bold if expanded/today) + optional Today
 *     pill on the left, blue count badge + chevron on the right.
 *   - Expanded state: grey container holding white Day Items
 *     (time + label + arrow). Item gap 6px, item padding 6/10.
 *   - Optional divider + "View all" footer link.
 *
 * Reusable: caller can fully control expansion via `expandedDates` +
 * `onToggle` (controlled mode), OR leave them undefined to use built-in
 * state (uncontrolled). Default expanded: today if it exists in the data,
 * otherwise the first group — so the widget never opens blank.
 */

import * as React from "react";

export interface DateGroupItem {
  id: number | string;
  /** Main row label, e.g. "Plumbing repair — Smith Residence". */
  label: string;
  /** Optional secondary line under the label. */
  subLabel?: string | null;
  /** "10:00 AM" or "1:30 PM" — short, left-aligned. Null = no time slot rendered. */
  time?: string | null;
  /** Click handler on the row arrow. */
  onClick?: () => void;
}

export interface DateGroup {
  date: string; // ISO YYYY-MM-DD
  /**
   * True total for the day. Defaults to items.length when omitted. When it
   * exceeds items.length (items capped server-side), the badge shows the true
   * count and the expanded view shows a "+N more" affordance.
   */
  count?: number;
  items: DateGroupItem[];
  /** Optional handler for the "+N more" link when the day is truncated. */
  onViewMore?: () => void;
}

export interface DateGroupedListProps {
  groups: DateGroup[];
  loading?: boolean;
  /**
   * Override the column header labels (default "Date" / "Items"). Set to
   * null to hide the header entirely.
   */
  columnHeaders?: { left: string; right: string } | null;
  /** Controlled: set of dates rendered expanded. Omit for uncontrolled mode. */
  expandedDates?: Set<string>;
  /** Controlled: callback when a date row is toggled. Omit for uncontrolled. */
  onToggle?: (date: string) => void;
  emptyMessage?: string;
}

const ISO_TODAY = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

function pickDefaultExpanded(groups: DateGroup[]): Set<string> {
  if (groups.length === 0) return new Set();
  const today = ISO_TODAY();
  const hasToday = groups.some((g) => g.date === today);
  return new Set([hasToday ? today : groups[0].date]);
}

export function DateGroupedList({
  groups,
  loading,
  columnHeaders = { left: "Date", right: "Items" },
  expandedDates,
  onToggle,
  emptyMessage = "Nothing scheduled",
}: DateGroupedListProps) {
  const isControlled = expandedDates !== undefined;
  const [internalExpanded, setInternalExpanded] = React.useState<Set<string>>(
    () => pickDefaultExpanded(groups),
  );

  // When uncontrolled and the data loads (groups goes from empty → populated),
  // re-seed the default expanded set so the first paint isn't blank.
  React.useEffect(() => {
    if (!isControlled && internalExpanded.size === 0 && groups.length > 0) {
      setInternalExpanded(pickDefaultExpanded(groups));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isControlled, groups.length]);

  const expanded = isControlled ? expandedDates! : internalExpanded;
  const handleToggle = React.useCallback(
    (date: string) => {
      if (isControlled) {
        onToggle?.(date);
        return;
      }
      setInternalExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(date)) next.delete(date);
        else next.add(date);
        return next;
      });
    },
    [isControlled, onToggle],
  );

  if (loading) return <DateGroupedListSkeleton hasHeader={columnHeaders !== null} />;
  if (groups.length === 0) {
    return <div className="text-xs text-gray-400">{emptyMessage}</div>;
  }

  const today = ISO_TODAY();

  // Rendered as a fragment so sticky elements become direct children of
  // WidgetCard's overflow-y-auto body — that's the scroll container they
  // need to stick within. Wrapping in a div with h-full breaks sticky
  // because the containing block (the wrapper) bounds the stickiness and
  // moves with the outer scroll, dragging the sticky header off-screen.
  return (
    <>
      {columnHeaders && (
        <div className="sticky top-0 z-10 bg-white pb-2">
          <div className="flex items-center justify-between bg-gray-50 rounded px-2.5 py-1.5">
            <span className="text-[10px] font-semibold tracking-wide text-gray-500">
              {columnHeaders.left}
            </span>
            <span className="text-[10px] font-semibold tracking-wide text-gray-500">
              {columnHeaders.right}
            </span>
          </div>
        </div>
      )}

      <ul className="flex flex-col gap-0.5">
        {groups.map((group) => (
          <li key={group.date}>
            <Group
              group={group}
              isToday={group.date === today}
              isExpanded={expanded.has(group.date)}
              onToggle={handleToggle}
            />
          </li>
        ))}
      </ul>
    </>
  );
}

function Group({
  group,
  isToday,
  isExpanded,
  onToggle,
}: {
  group: DateGroup;
  isToday: boolean;
  isExpanded: boolean;
  onToggle: (date: string) => void;
}) {
  const total = group.count ?? group.items.length;
  const hiddenCount = Math.max(0, total - group.items.length);
  return (
    <div>
      <button
        type="button"
        className="w-full flex items-center justify-between px-2 py-1.5 text-left rounded cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => onToggle(group.date)}
        aria-expanded={isExpanded}
      >
        <span className="flex items-center gap-1.5 min-w-0">
          <span
            className={`text-[11px] text-gray-800 truncate ${
              isExpanded || isToday ? "font-semibold" : "font-normal"
            }`}
          >
            {formatDate(group.date)}
          </span>
          {isToday && (
            <span className="px-1.5 py-px text-[8px] font-semibold tracking-wide rounded-full bg-blue-100 text-blue-800 flex-shrink-0">
              Today
            </span>
          )}
        </span>
        <span className="flex items-center gap-1.5 flex-shrink-0">
          <span className="inline-flex items-center justify-center min-w-[20px] h-[16px] px-1.5 rounded-full bg-blue-500 text-white text-[10px] font-semibold tabular-nums leading-none">
            {group.count ?? group.items.length}
          </span>
          <span
            aria-hidden
            className={`text-[13px] text-gray-400 leading-none transition-transform ${
              isExpanded ? "rotate-90" : ""
            }`}
          >
            ›
          </span>
        </span>
      </button>

      {isExpanded && group.items.length > 0 && (
        <div className="mt-1 mb-1 bg-gray-50 rounded p-2 flex flex-col gap-1.5">
          {group.items.map((item) => (
            <DayItem key={item.id} item={item} />
          ))}
          {hiddenCount > 0 &&
            (group.onViewMore ? (
              <button
                type="button"
                onClick={group.onViewMore}
                className="text-[10px] font-semibold text-blue-600 hover:text-blue-700 text-center py-1"
              >
                +{hiddenCount} more
              </button>
            ) : (
              <div className="text-[10px] text-gray-400 text-center py-1">
                +{hiddenCount} more
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

function DayItem({ item }: { item: DateGroupItem }) {
  const isClickable = typeof item.onClick === "function";
  return (
    <div
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={item.onClick}
      onKeyDown={
        isClickable
          ? (e) => (e.key === "Enter" || e.key === " ") && item.onClick?.()
          : undefined
      }
      className={`flex items-center gap-2 bg-white rounded px-2.5 py-1.5 ${
        isClickable ? "cursor-pointer hover:bg-blue-50" : ""
      }`}
      title={item.label}
    >
      {item.time && (
        <span
          className="text-[10px] font-semibold text-gray-500 tabular-nums flex-shrink-0"
          style={{ minWidth: 48, letterSpacing: "0.2px" }}
        >
          {item.time}
        </span>
      )}
      <p
        className="flex-1 min-w-0 text-[10px] text-gray-800 overflow-hidden leading-snug"
        style={{
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
        }}
      >
        {item.label}
      </p>
      {isClickable && (
        <span aria-hidden className="text-[11px] text-blue-500 flex-shrink-0">
          →
        </span>
      )}
    </div>
  );
}

function formatDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function DateGroupedListSkeleton({ hasHeader }: { hasHeader: boolean }) {
  return (
    <div className="flex flex-col gap-3 animate-pulse">
      {hasHeader && <div className="h-5 bg-gray-100 rounded" />}
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex items-center justify-between px-2 py-1.5">
          <span className="h-3 w-24 bg-gray-200 rounded" />
          <span className="h-4 w-6 bg-gray-200 rounded-full" />
        </div>
      ))}
    </div>
  );
}
